import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, inject, input, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { AuthenticationService } from 'core/services/auth/authentication.service';
import { filter, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AbstractFilterDropdownComponent } from 'shared/components/atoms';
import { UserSites } from 'shared/types';
import { FilterDropdownComponent } from '../filter-dropdown/filter-dropdown.component';

@Component({
  selector: 'app-users-filter-dropdown',
  templateUrl: './users-filter-dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UsersFilterDropdownComponent),
      multi: true,
    },
  ],
  imports: [FilterDropdownComponent, ReactiveFormsModule, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersFilterDropdownComponent extends AbstractFilterDropdownComponent<UserSites['user']['id']> {
  @Input() public label = '';
  @Input() public roleIds?: Set<number>;
  public excludedRoleIds = input<Set<number>>();
  private authService = inject(AuthenticationService);
  public usersData$ = this.contextService.siteId.asObservable().pipe(
    filter((siteId) => siteId !== null),
    switchMap((siteId) => this.authService.getSiteUsers(siteId)),
    map(({ value }) => {
      const uniqueUsers = new Map<
        string,
        {
          value: string;
          roleId: number;
          label: string;
        }
      >();

      value.userSites
        .filter((site) => !this.excludedRoleIds() || !this.excludedRoleIds()?.has(site.role.databaseId))
        .forEach((site) => {
          const userId = site.user.id;
          const existingUser = uniqueUsers.get(userId);
          const shouldUpdate = !existingUser || (this.roleIds && this.roleIds.has(site.role.databaseId));

          if (shouldUpdate) {
            uniqueUsers.set(userId, {
              value: userId,
              roleId: site.role.databaseId,
              label: `${site.user.firstName} ${site.user.lastName}`,
            });
          }
        });

      const users = Array.from(uniqueUsers.values());
      return this.roleIds ? users.filter((user) => this.roleIds?.has(user.roleId)) : users;
    })
  );

  constructor() {
    super();
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userId: UserSites['user']['id']) => {
        this.onChange(userId);
      });
  }
}
